import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import {
  getActiveDraft, setActiveDraft as setActiveDraftApi,
  clearActiveDraft as clearActiveDraftApi,
  fileKeys, type ActiveDraftPointer,
} from "@/lib/files-api";

/**
 * useActiveDraft — the single source of truth for the floating chip and any
 * page that wants to add files to "the current share". Polls every 30s so the
 * chip stays in sync across tabs/windows.
 */
export function useActiveDraft() {
  const { sessionId } = useAuth();
  const qc = useQueryClient();
  const key = [...fileKeys.activeDraft(), sessionId];

  const query = useQuery<ActiveDraftPointer | null>({
    queryKey: key,
    queryFn: () => getActiveDraft(sessionId),
    enabled: !!sessionId,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const setDraft = useMutation({
    mutationFn: (bundleId: string) => setActiveDraftApi(bundleId, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.activeDraft() }),
  });

  const clearDraft = useMutation({
    mutationFn: () => clearActiveDraftApi(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.activeDraft() }),
  });

  return {
    draft: query.data ?? null,
    isLoading: query.isLoading,
    setDraft: setDraft.mutateAsync,
    clearDraft: clearDraft.mutateAsync,
    refresh: () => qc.invalidateQueries({ queryKey: fileKeys.activeDraft() }),
  };
}
